import { getFormalOffersMaxAvailableCash } from '@helpers/FormalOffers/getFormalOffersMaxAvailableCash';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import {
    fetchGetStateByStateKey,
    fetchPostActiveConsent,
    GetFormalOffersResponse,
    GetFundingRequirementsCreditFacilityResponse
} from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import { useMutation, useQueries } from '@tanstack/react-query';
import { FormattedDate, FormattedMessage } from 'react-intl';

import messages from './AccessUserApprovedView.messages';
import styles from './AccessUserApprovedView.module.css';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import { FooterSectionWrapper } from '../../../pageStructure/footerSectionWrapper/FooterSectionwraper';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { MainSectionWrapper } from '../../../pageStructure/mainSectionWrapper';
import mainSectionWrapperStyles from '../../../pageStructure/mainSectionWrapper/MainSectionWrapper.module.css';
import { TitleSectionWrapper } from '../../../pageStructure/titleSectionWrapper/TitleSectionWrapper';
import { TrustPilot } from '../../../TrustPilot/TrustPilot';
import { MoneyNoDecimals } from '../../../ui/moneyNoDecimals';
import headerStyles from '../../approvedView/approvalHeader/ApprovalHeader.module.css';
import { getTimeOutDate } from '../../MyOffers.helpers';
import { LoanSummary } from '../flexiApprovedView/loanSummary/LoanSummary';

export const SelectedOfferView = ({
    selectedFormalOffer,
    validateAndStartDrawdown
}: {
    selectedFormalOffer: NonNullable<
        GetFormalOffersResponse['data']
    >['formal_offers'][number];
    validateAndStartDrawdown: (
        selectedOfferId: string,
        drawdownAmount: number
    ) => void;
}) => {
    const stateKey = useStateKey();
    const productType = selectedFormalOffer.product_type!;
    const offerId = selectedFormalOffer.offer_id!;

    const queries = useQueries({
        queries: [
            {
                queryKey: [
                    'funding_requirements',
                    stateKey,
                    productType,
                    offerId
                ],
                queryFn: async () => {
                    // eslint-disable-next-line iwoca/prefer-lapi-client
                    const response = await fetch(
                        `/api/lending/edge/funding_requirements/${stateKey}/${productType}/${offerId}/`
                    );
                    return (await response.json()) as GetFundingRequirementsCreditFacilityResponse;
                }
            },
            {
                queryKey: ['customer_state', stateKey],
                queryFn: () => fetchGetStateByStateKey({ stateKey })
            }
        ]
    });

    const [fundingRequirementsQuery, customerStateQuery] = queries;

    const fundingRequirements = fundingRequirementsQuery.data?.data;
    const customerState = customerStateQuery.data?.data;

    const isLoading = queries.some(
        (query) => query.isLoading && query.isFetching
    );

    const isError = queries.some((query) => query.isError);

    const { mutate, isLoading: isUpdating } = useMutation({
        mutationFn: fetchPostActiveConsent,
        onSuccess: () => {
            validateAndStartDrawdown(offerId, drawdownAmount);
        },
        onError: () => {
            throw Error('Failed to update active consent');
        }
    });

    if (isLoading) {
        return (
            <MainContentPending data-testid="access-user-approved-view-loading" />
        );
    }

    if (isError) {
        return <FallbackErrorModal />;
    }

    const businessName =
        customerState?.application?.company?.registered_company_name;
    const approvalStatusTimeOut = getTimeOutDate([selectedFormalOffer]);
    const maxCashAmount = getFormalOffersMaxAvailableCash([
        selectedFormalOffer
    ]);
    const drawdownAmount = selectedFormalOffer.available_cash || 0;
    const isActiveConsentRequired =
        fundingRequirements?.find(
            (item) =>
                item.recommended_resolution_method === 'get_active_consent'
        )?.status === 'required';

    return (
        <>
            <TitleSectionWrapper
                wrapperInnerClassName={headerStyles.titleInnerWrapper}
            >
                <div
                    className={headerStyles.header}
                    data-testid="approval-header"
                >
                    <div className={headerStyles.headerCustomerIntroduction}>
                        <FormattedMessage
                            {...messages.header}
                            values={{
                                businessName: <span>{businessName}</span>,
                                offeredAmount: (
                                    <span className={styles.headerBlue}>
                                        <MoneyNoDecimals
                                            value={maxCashAmount}
                                        />
                                    </span>
                                ),
                                timeoutDate: (
                                    <span className={styles.headerBlue}>
                                        <FormattedDate
                                            value={approvalStatusTimeOut}
                                            day="2-digit"
                                            month="long"
                                            year="numeric"
                                        />
                                    </span>
                                )
                            }}
                        />
                    </div>
                </div>
            </TitleSectionWrapper>
            <MainSectionWrapper
                className={styles.mainSection}
                innerWrapperClassName={mainSectionWrapperStyles.innerWrapper}
            >
                <>
                    <div className={styles.displayTableSection}>
                        <LoanSummary
                            drawdownAmount={drawdownAmount}
                            selectedOffer={selectedFormalOffer}
                            isAccessUser
                        />
                    </div>
                    <div
                        className={styles.ctaSection}
                        data-testid="loan-builder-cta"
                    >
                        <div className={styles.ctaHeplerText}>
                            {isActiveConsentRequired ? (
                                <FormattedMessage
                                    {...messages.activeConsentCTAInfo}
                                />
                            ) : (
                                <FormattedMessage
                                    {...messages.CTAinfo}
                                    values={{
                                        businessName: (
                                            <span>{businessName}</span>
                                        )
                                    }}
                                />
                            )}
                        </div>
                        <div>
                            <Button
                                variant="success"
                                type="submit"
                                size="large"
                                data-action-name="Approval continue button"
                                disabled={isUpdating}
                                onClick={() => {
                                    if (isActiveConsentRequired) {
                                        mutate({
                                            stateKey,
                                            offerId,
                                            body: {
                                                data: {
                                                    outcome: 'APPROVED'
                                                }
                                            }
                                        });
                                    } else {
                                        validateAndStartDrawdown(
                                            offerId,
                                            drawdownAmount
                                        );
                                    }
                                }}
                            >
                                <FormattedMessage
                                    {...(isActiveConsentRequired
                                        ? messages.activeConsentCTA
                                        : messages.cta)}
                                />
                            </Button>
                        </div>
                    </div>
                </>
            </MainSectionWrapper>
            <FooterSectionWrapper>
                {getCountry() === 'UK' && <TrustPilot />}
            </FooterSectionWrapper>
        </>
    );
};
